import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/integrations/supabase/client'
import {
  CheckCircle, XCircle, Pause, DollarSign, Users,
  ChevronDown, Search, Loader2, Settings,
  TrendingUp, ArrowLeft, RefreshCw, AlertTriangle, Save
} from 'lucide-react'

interface CommissionTier {
  id: string
  tier_key: string
  label: string
  commission_rate: number
  min_referrals: number
  is_active: boolean
  sort_order: number
}

interface AffiliateSetting {
  key: string
  value: string
}

interface Partner {
  id: string; code: string; full_name: string; email: string
  status: string; commission_rate: number; promo_channel: string | null
  total_active_referrals: number; total_earned_cents: number
  total_paid_cents: number; pending_payout_cents: number
  applied_at: string; approved_at: string | null; notes: string | null
  website: string | null; payout_method: string | null;
  payout_details?: any;
  subscriber_floor_active?: boolean;
}

interface DetailData {
  partner: Partner
  referrals: any[]
  ledger: any[]
  payouts: any[]
  milestones: any[]
}

const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`

// Fetches tier rates from DB for the rate select dropdown in the detail view
function DynamicRateOptions({ currentRate }: { currentRate: number }) {
  const [rates, setRates] = useState<number[]>([currentRate])
  useEffect(() => {
    supabase
      .from('affiliate_commission_tiers' as any)
      .select('commission_rate')
      .eq('is_active', true)
      .order('sort_order', { ascending: true })
      .then(({ data }) => {
        if (data && data.length > 0) {
          const tierRates = data.map((d: any) => Number(d.commission_rate))
          const merged = Array.from(new Set([currentRate, ...tierRates])).sort((a, b) => a - b)
          setRates(merged)
        }
      })
  }, [currentRate])
  return (
    <>
      {rates.map(r => (
        <option key={r} value={r}>{r}%{r === currentRate ? ' (current)' : ''}</option>
      ))}
    </>
  )
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; color: string }> = {
    pending: { bg: '#e0e7ff', color: '#3730a3' },
    approved: { bg: '#dcfce7', color: '#166534' },
    suspended: { bg: '#fef3c7', color: '#92400e' },
    rejected: { bg: '#fee2e2', color: '#991b1b' },
  }
  const c = map[status] || { bg: '#f1f5f9', color: '#475569' }
  return (
    <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '3px 10px', borderRadius: 20, background: c.bg, color: c.color }}>
      {status}
    </span>
  )
}

function AffiliatDetailView({ affiliateId, onBack }: { affiliateId: string; onBack: () => void }) {
  const { t } = useTranslation()
  const [data, setData] = useState<DetailData | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [payoutRef, setPayoutRef] = useState('')
  const [showPayoutForm, setShowPayoutForm] = useState(false)
  const [toast, setToast] = useState('')

  const load = useCallback(async () => {
    const { data: d } = await supabase.functions.invoke('affiliate-admin', {
      body: { action: 'get_affiliate', affiliate_id: affiliateId }
    })
    setData(d)
    setLoading(false)
  }, [affiliateId])

  useEffect(() => { load() }, [load])

  const doAction = async (action: string, extra: Record<string, any> = {}) => {
    setActionLoading(true)
    try {
      const { error } = await supabase.functions.invoke('affiliate-admin', {
        body: { action, affiliate_id: affiliateId, ...extra }
      })
      if (error) throw error
      await load()
      setToast(`${action} successful`)
      setTimeout(() => setToast(''), 3000)
    } catch (err: any) {
      setToast(`Error: ${err.message}`)
      setTimeout(() => setToast(''), 4000)
    } finally {
      setActionLoading(false)
    }
  }

  const triggerPayout = async () => {
    setActionLoading(true)
    try {
      const { error } = await supabase.functions.invoke('affiliate-admin', {
        body: { action: 'trigger_payout', affiliate_id: affiliateId, payout_reference: payoutRef }
      })
      if (error) throw error
      setShowPayoutForm(false)
      setPayoutRef('')
      await load()
      setToast('Payout created')
      setTimeout(() => setToast(''), 3000)
    } catch (err: any) {
      setToast(`Error: ${err.message}`)
      setTimeout(() => setToast(''), 4000)
    } finally {
      setActionLoading(false)
    }
  }

  if (loading || !data) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60 }}>
      <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: '#4a7c5e' }} />
    </div>
  )

  const { partner, referrals, ledger, payouts } = data

  return (
    <div>
      <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#8a94a6', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20, fontSize: 14 }}>
        <ArrowLeft size={15} /> {t('affiliates.admin.detail.back')}
      </button>

      {toast && (
        <div style={{ background: toast.startsWith('Error') ? '#fee2e2' : '#dcfce7', border: `1px solid ${toast.startsWith('Error') ? '#fecaca' : '#bbf7d0'}`, borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 13, fontWeight: 600, color: toast.startsWith('Error') ? '#991b1b' : '#166534' }}>
          {toast}
        </div>
      )}

      {/* Partner header */}
      <div style={{ background: '#1a2740', borderRadius: 18, padding: '24px 24px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 900, color: '#fff', margin: '0 0 6px' }}>{partner.full_name}</h2>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', margin: '0 0 10px' }}>{partner.email}</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <StatusBadge status={partner.status} />
            <span style={{ fontSize: 11, background: 'rgba(74,124,94,0.25)', color: '#4a7c5e', padding: '3px 10px', borderRadius: 20, fontWeight: 700 }}>
              CODE: {partner.code}
            </span>
            <span style={{ fontSize: 11, background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', padding: '3px 10px', borderRadius: 20, fontWeight: 700 }}>
              {partner.commission_rate}% commission
            </span>
            {(partner as any).subscriber_floor_active && (
              <span style={{ fontSize: 10, background: 'rgba(212,168,67,0.25)', color: '#d4a843', padding: '3px 10px', borderRadius: 20, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                🎁 Subscriber Floor — not natural tier
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {partner.status === 'pending' && (
            <>
              <button onClick={() => doAction('approve')} disabled={actionLoading}
                style={{ padding: '9px 18px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <CheckCircle size={14} /> {t('affiliates.admin.actions.approve')}
              </button>
              <button onClick={() => doAction('reject')} disabled={actionLoading}
                style={{ padding: '9px 18px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <XCircle size={14} /> {t('affiliates.admin.actions.reject')}
              </button>
            </>
          )}
          {partner.status === 'approved' && (
            <>
              <select onChange={e => doAction('set_rate', { rate: Number(e.target.value) })}
                value={partner.commission_rate}
                title={(partner as any).subscriber_floor_active ? '⚠️ Subscriber floor active — the next subscription event will re-floor them to 30%. Set rate here only for manual overrides.' : 'Set commission rate'}
                style={{ padding: '8px 12px', borderRadius: 10, border: `1px solid ${(partner as any).subscriber_floor_active ? 'rgba(212,168,67,0.5)' : 'rgba(255,255,255,0.2)'}`, background: 'rgba(255,255,255,0.08)', color: '#fff', fontWeight: 700, fontSize: 13 }}>
                <DynamicRateOptions currentRate={partner.commission_rate} />
              </select>
              <button onClick={() => doAction('suspend')} disabled={actionLoading}
                style={{ padding: '9px 16px', background: 'rgba(245,158,11,0.2)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Pause size={14} /> {t('affiliates.admin.actions.suspend')}
              </button>
              <button onClick={() => setShowPayoutForm(true)} disabled={actionLoading || partner.pending_payout_cents <= 0}
                style={{ padding: '9px 18px', background: 'linear-gradient(135deg, #4a7c5e, #2d5a3d)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: partner.pending_payout_cents <= 0 ? 'not-allowed' : 'pointer', opacity: partner.pending_payout_cents <= 0 ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                <DollarSign size={14} /> {t('affiliates.admin.actions.payout', { amount: fmt(partner.pending_payout_cents) })}
              </button>
            </>
          )}
          {partner.status === 'suspended' && (
            <button onClick={() => doAction('approve')} disabled={actionLoading}
              style={{ padding: '9px 18px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              {t('affiliates.admin.actions.reinstate')}
            </button>
          )}
        </div>
      </div>

      {/* Payout form */}
      <AnimatePresence>
        {showPayoutForm && (
          <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ background: '#fdf6e9', borderRadius: 14, padding: '20px 20px', marginBottom: 20, border: '1px solid #c9903a30', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={{ fontSize: 11, fontWeight: 800, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
                {t('affiliates.admin.payout.referenceLabel')}
              </label>
              <input value={payoutRef} onChange={e => setPayoutRef(e.target.value)}
                placeholder={t('affiliates.admin.payout.referencePlaceholder')}
                style={{ width: '100%', height: 44, padding: '0 12px', borderRadius: 10, border: '2px solid #e2e8f0', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <button onClick={triggerPayout} disabled={actionLoading}
              style={{ padding: '11px 24px', background: 'linear-gradient(135deg, #c9903a, #a87830)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
              {actionLoading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : t('affiliates.admin.payout.confirm', { amount: fmt(partner.pending_payout_cents) })}
            </button>
            <button onClick={() => setShowPayoutForm(false)} style={{ padding: '11px 16px', background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 10, cursor: 'pointer', color: '#8a94a6', fontWeight: 600 }}>
              {t('affiliates.admin.payout.cancel')}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Application Details */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, marginBottom: 24 }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', border: '1px solid #e2e8f0' }}>
          <h4 style={{ fontSize: 11, fontWeight: 800, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>Promotion Details</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#8a94a6', marginBottom: 2 }}>Website / Channel</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1a2740' }}>
                {partner.website ? (
                  <a href={partner.website} target="_blank" rel="noopener noreferrer" style={{ color: '#4a7c5e', textDecoration: 'none' }}>
                    {partner.website} ↗
                  </a>
                ) : 'Not provided'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#8a94a6', marginBottom: 2 }}>Main Channel</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1a2740', textTransform: 'capitalize' }}>
                {partner.promo_channel?.replace('_', ' ') || 'Not specified'}
              </div>
            </div>
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', border: '1px solid #e2e8f0' }}>
          <h4 style={{ fontSize: 11, fontWeight: 800, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>Payout Preference</h4>
          <div>
            <div style={{ fontSize: 12, color: '#8a94a6', marginBottom: 2 }}>Method</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1a2740', textTransform: 'capitalize' }}>
              {partner.payout_method?.replace('_', ' ') || 'Not specified'}
            </div>
            {partner.payout_method === 'bank_transfer' && (
              <div style={{ marginTop: 10, padding: '10px 12px', background: '#f8fafc', borderRadius: 8, fontFamily: 'monospace', fontSize: 12, color: '#475569', lineHeight: 1.7 }}>
                {(partner as any).payout_details?.account_name && <div>{(partner as any).payout_details.account_name}</div>}
                {(partner as any).payout_details?.bank_name && <div>{(partner as any).payout_details.bank_name}</div>}
                {((partner as any).payout_details?.bank_code || (partner as any).payout_details?.sort_code) && (
                  <div>{(partner as any).payout_details?.bank_code || (partner as any).payout_details?.sort_code} · {(partner as any).payout_details?.account_number}</div>
                )}
                {(partner as any).payout_details?.swift_bic && <div>SWIFT: {(partner as any).payout_details.swift_bic}</div>}
                {(partner as any).payout_details?.bank_currency && <div>Currency: {(partner as any).payout_details.bank_currency}</div>}
                {(partner as any).payout_details?.paypal_email && <div>{(partner as any).payout_details.paypal_email}</div>}
                {(partner as any).payout_details?.wise_email && <div>{(partner as any).payout_details.wise_email}</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: t('affiliates.admin.stats.totalEarned'), value: fmt(partner.total_earned_cents), color: '#4a7c5e' },
          { label: t('affiliates.admin.stats.pending'), value: fmt(partner.pending_payout_cents), color: '#c9903a' },
          { label: t('affiliates.admin.stats.paid'), value: fmt(partner.total_paid_cents), color: '#10b981' },
          { label: t('affiliates.admin.stats.referrals'), value: String(partner.total_active_referrals), color: '#3b82f6' },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', borderRadius: 14, padding: '16px 18px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#8a94a6', textTransform: 'uppercase', marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Referrals table */}
      {referrals.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ padding: '16px 20px 0' }}>
            <h4 style={{ fontWeight: 800, color: '#1a2740', margin: '0 0 12px', fontSize: 15 }}>{t('affiliates.admin.detail.referrals')}</h4>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#faf7f2' }}>
                  {['Plan', 'Platform', 'Monthly', 'Commission/mo', 'Status', 'Since'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {referrals.map((r: any, i: number) => (
                  <tr key={r.id} style={{ borderTop: '1px solid #e2e8f0', background: i % 2 === 0 ? '#fff' : '#fcfcfe' }}>
                    <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 600, color: '#1a2740' }}>{r.plan_key}</td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: '#475569' }}>{r.platform}</td>
                    <td style={{ padding: '10px 16px', fontSize: 13, color: '#475569' }}>{fmt(r.monthly_value_cents)}</td>
                    <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 700, color: '#4a7c5e' }}>
                      {fmt(Math.round(r.monthly_value_cents * (r.commission_rate_at_referral / 100)))}
                    </td>
                    <td style={{ padding: '10px 16px' }}><StatusBadge status={r.status} /></td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: '#8a94a6' }}>{r.first_payment_at ? new Date(r.first_payment_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Notes */}
      {partner.notes && (
        <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 12, padding: '14px 18px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <AlertTriangle size={16} color="#92400e" style={{ flexShrink: 0, marginTop: 2 }} />
          <p style={{ margin: 0, fontSize: 13, color: '#92400e', lineHeight: 1.6 }}>{partner.notes}</p>
        </div>
      )}
    </div>
  )
}

// ── CommissionTierSettings — admin edits tier rates/thresholds + discount ───────
function CommissionTierSettings() {
  const [tiers, setTiers] = useState<CommissionTier[]>([])
  const [discountPct, setDiscountPct] = useState('5')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [toast, setToast] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.functions.invoke('affiliate-admin', {
      body: { action: 'get_tiers' }
    })
    if (data?.tiers) setTiers(data.tiers)
    const discount = data?.settings?.find((s: AffiliateSetting) => s.key === 'customer_discount_pct')
    if (discount) setDiscountPct(discount.value)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const updateTier = (id: string, field: keyof CommissionTier, value: any) => {
    setTiers(prev => prev.map(t => t.id === id ? { ...t, [field]: value } : t))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const { error } = await supabase.functions.invoke('affiliate-admin', {
        body: {
          action: 'update_tiers',
          tiers,
          settings: [{ key: 'customer_discount_pct', value: discountPct }],
        }
      })
      if (error) throw error
      setSaved(true)
      setToast('Tier settings saved successfully')
      setTimeout(() => { setSaved(false); setToast('') }, 3000)
    } catch (err: any) {
      setToast(`Error: ${err.message}`)
      setTimeout(() => setToast(''), 4000)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
      <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#4a7c5e' }} />
    </div>
  )

  const inputSt: React.CSSProperties = {
    height: 40, padding: '0 10px', borderRadius: 8,
    border: '2px solid #e2e8f0', fontSize: 14,
    background: '#fff', color: '#1a2740', width: '100%', boxSizing: 'border-box',
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {toast && (
        <div style={{
          background: toast.startsWith('Error') ? '#fee2e2' : '#dcfce7',
          border: `1px solid ${toast.startsWith('Error') ? '#fecaca' : '#bbf7d0'}`,
          borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 600,
          color: toast.startsWith('Error') ? '#991b1b' : '#166534',
        }}>{toast}</div>
      )}

      {/* Customer Discount */}
      <div style={{ background: '#fff', borderRadius: 14, padding: '20px 20px', border: '1px solid #e2e8f0' }}>
        <h4 style={{ fontWeight: 800, color: '#1a2740', margin: '0 0 14px', fontSize: 15 }}>
          Customer Discount (Stripe Coupon)
        </h4>
        <p style={{ fontSize: 13, color: '#8a94a6', margin: '0 0 14px' }}>
          All new customers referred by any affiliate will receive this % off their first payment.
          Changing this value only affects new approvals — existing coupons are not modified.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, maxWidth: 280 }}>
          <input
            type="number" min="1" max="100" value={discountPct}
            onChange={e => setDiscountPct(e.target.value)}
            style={{ ...inputSt, width: 80 }} />
          <span style={{ fontSize: 18, fontWeight: 900, color: '#1a2740' }}>% off first payment</span>
        </div>
      </div>

      {/* Commission Tiers */}
      <div style={{ background: '#fff', borderRadius: 14, padding: '20px 20px', border: '1px solid #e2e8f0' }}>
        <h4 style={{ fontWeight: 800, color: '#1a2740', margin: '0 0 6px', fontSize: 15 }}>
          Commission Tiers
        </h4>
        <p style={{ fontSize: 13, color: '#8a94a6', margin: '0 0 18px' }}>
          These values determine what affiliates earn and what milestone thresholds unlock each rate.
          Changes are reflected immediately on all public-facing affiliate pages.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {tiers.map(tier => (
            <div key={tier.id} style={{
              display: 'grid', gridTemplateColumns: '1fr 100px 120px 80px',
              gap: 12, alignItems: 'center', padding: '14px 16px',
              background: tier.is_active ? '#f8fcfa' : '#fafafa',
              borderRadius: 10, border: `1px solid ${tier.is_active ? '#e8f2ec' : '#e2e8f0'}`,
            }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, color: '#8a94a6', textTransform: 'uppercase', marginBottom: 4 }}>Label</div>
                <input
                  value={tier.label}
                  onChange={e => updateTier(tier.id, 'label', e.target.value)}
                  style={inputSt} />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, color: '#8a94a6', textTransform: 'uppercase', marginBottom: 4 }}>Rate %</div>
                <input
                  type="number" min="1" max="100"
                  value={tier.commission_rate}
                  onChange={e => updateTier(tier.id, 'commission_rate', Number(e.target.value))}
                  style={inputSt} />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, color: '#8a94a6', textTransform: 'uppercase', marginBottom: 4 }}>
                  Min Referrals to Unlock
                </div>
                <input
                  type="number" min="0"
                  value={tier.min_referrals}
                  onChange={e => updateTier(tier.id, 'min_referrals', Number(e.target.value))}
                  style={inputSt} />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, color: '#8a94a6', textTransform: 'uppercase', marginBottom: 4 }}>Active</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox" checked={tier.is_active}
                    onChange={e => updateTier(tier.id, 'is_active', e.target.checked)}
                    style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  <span style={{ fontSize: 13, color: '#475569' }}>{tier.is_active ? 'Yes' : 'No'}</span>
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button onClick={handleSave} disabled={saving}
        style={{
          padding: '13px 24px', borderRadius: 11,
          background: saved ? '#10b981' : 'linear-gradient(135deg, #4a7c5e, #2d5a3d)',
          color: '#fff', border: 'none', fontWeight: 800, fontSize: 15,
          cursor: saving ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', gap: 8,
          transition: 'background 0.3s', alignSelf: 'flex-start',
        }}>
        {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> :
          saved ? <CheckCircle size={16} /> : <Save size={16} />}
        {saved ? 'Saved!' : saving ? 'Saving…' : 'Save Tier Settings'}
      </button>
    </div>
  )
}

// ─── MAIN ADMIN COMPONENT ─────────────────────────────────────────────────────
export function AffiliateManagement() {
  const { t } = useTranslation()
  const [partners, setPartners] = useState<Partner[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'suspended' | 'rejected'>('all')
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [adminTab, setAdminTab] = useState<'partners' | 'tiers'>('partners')
  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.functions.invoke('affiliate-admin', {
      body: { action: 'list_all' }
    })
    setPartners(data?.affiliates || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const filtered = partners.filter(p => {
    const matchesFilter = filter === 'all' || p.status === filter
    const matchesSearch = !search || p.full_name.toLowerCase().includes(search.toLowerCase()) || p.email.toLowerCase().includes(search.toLowerCase()) || p.code.toLowerCase().includes(search.toLowerCase())
    return matchesFilter && matchesSearch
  })

  const pendingCount = partners.filter(p => p.status === 'pending').length

  if (selectedId) {
    return <AffiliatDetailView affiliateId={selectedId} onBack={() => { setSelectedId(null); load() }} />
  }

  return (
    <div className="p-6 space-y-6">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 900, color: '#1a2740', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <DollarSign size={24} color="#4a7c5e" />
            Affiliate Partner Management
          </h2>
          <p style={{ color: '#8a94a6', margin: 0, fontSize: 14 }}>Manage affiliate applications, approvals, payouts, and commission tier settings</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#475569' }}>
            <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            Refresh
          </button>
        </div>
      </div>

      {/* Sub-tabs: Partners | Tier Settings */}
      <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 12, padding: 4, border: '1px solid #e2e8f0', alignSelf: 'flex-start', width: 'fit-content' }}>
        {([['partners', 'Affiliate Partners', <Users size={14} />], ['tiers', 'Commission Tiers', <Settings size={14} />]] as const).map(([id, label, icon]) => (
          <button key={id} onClick={() => setAdminTab(id as any)}
            style={{
              padding: '8px 18px', borderRadius: 9, border: 'none',
              background: adminTab === id ? '#1a2740' : 'transparent',
              color: adminTab === id ? '#fff' : '#475569',
              fontWeight: 700, fontSize: 13, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
              transition: 'all 0.15s',
            }}>
            {icon} {label}
          </button>
        ))}
      </div>
      {/* Conditionally show Tier Settings vs Partners list */}
      {adminTab === 'tiers' ? (
        <CommissionTierSettings />
      ) : (
        <>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            {[
              { label: t('affiliates.admin.summary.total'), value: partners.length, color: '#3b82f6' },
              { label: t('affiliates.admin.summary.pending'), value: pendingCount, color: '#f59e0b' },
              { label: t('affiliates.admin.summary.active'), value: partners.filter(p => p.status === 'approved').length, color: '#10b981' },
              { label: t('affiliates.admin.summary.totalOwed'), value: fmt(partners.reduce((s, p) => s + p.pending_payout_cents, 0)), color: '#4a7c5e' },
            ].map(s => (
              <div key={s.label} style={{ background: '#fff', borderRadius: 14, padding: '16px 18px', border: '1px solid #e2e8f0', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: 24, fontWeight: 900, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {pendingCount > 0 && (
            <div style={{ background: '#e0e7ff', border: '1px solid #a5b4fc', borderRadius: 12, padding: '12px 18px', display: 'flex', gap: 10, alignItems: 'center' }}>
              <AlertTriangle size={16} color="#3730a3" />
              <span style={{ fontSize: 14, fontWeight: 700, color: '#3730a3' }}>
                {t('affiliates.admin.pendingAlert', { count: pendingCount })}
              </span>
              <button onClick={() => setFilter('pending')} style={{ marginLeft: 'auto', padding: '5px 14px', background: '#3730a3', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                {t('affiliates.admin.reviewNow')}
              </button>
            </div>
          )}

          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
              <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#8a94a6' }} />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder={t('affiliates.admin.searchPlaceholder')}
                style={{ width: '100%', height: 40, paddingLeft: 36, paddingRight: 12, borderRadius: 10, border: '2px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {(['all', 'pending', 'approved', 'suspended', 'rejected'] as const).map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  style={{
                    padding: '7px 14px', borderRadius: 10, border: 'none',
                    background: filter === f ? '#1a2740' : '#f1f5f9',
                    color: filter === f ? '#fff' : '#475569',
                    fontWeight: 700, fontSize: 12, cursor: 'pointer',
                    textTransform: 'capitalize',
                  }}>
                  {t(`affiliates.admin.filter.${f}`)}
                  {f === 'pending' && pendingCount > 0 && (
                    <span style={{ marginLeft: 6, background: '#ef4444', color: '#fff', borderRadius: '50%', padding: '0 5px', fontSize: 10 }}>{pendingCount}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
              <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: '#4a7c5e' }} />
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px', background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0' }}>
              <Users size={36} style={{ opacity: 0.2, display: 'block', margin: '0 auto 12px' }} />
              <p style={{ color: '#8a94a6' }}>{t('affiliates.admin.empty')}</p>
            </div>
          ) : (
            <div style={{ background: '#fff', borderRadius: 18, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#faf7f2' }}>
                      {[
                        t('affiliates.admin.cols.affiliate'),
                        t('affiliates.admin.cols.code'),
                        t('affiliates.admin.cols.channel'),
                        t('affiliates.admin.cols.rate'),
                        t('affiliates.admin.cols.referrals'),
                        t('affiliates.admin.cols.pending'),
                        'Floor',
                        t('affiliates.admin.cols.status'),
                        t('affiliates.admin.cols.applied'),
                        '',
                      ].map((h, i) => (
                        <th key={i} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p, i) => (
                      <motion.tr
                        key={p.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        style={{ borderTop: '1px solid #e2e8f0', background: i % 2 === 0 ? '#fff' : '#fcfcfe', cursor: 'pointer' }}
                        onClick={() => setSelectedId(p.id)}
                      >
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: 700, color: '#1a2740', fontSize: 14 }}>{p.full_name}</div>
                          <div style={{ fontSize: 12, color: '#8a94a6' }}>{p.email}</div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ fontSize: 13, fontWeight: 800, color: '#4a7c5e', fontFamily: 'monospace' }}>{p.code}</span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 12, color: '#475569' }}>{p.promo_channel || '—'}</td>
                        <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 800, color: '#1a2740' }}>
                          {p.commission_rate}%
                          {(p as any).subscriber_floor_active && (
                            <span style={{ fontSize: 9, color: '#d4a843', display: 'block', fontWeight: 700, marginTop: 2 }}>🎁 SUB FLOOR</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: '#475569' }}>{p.total_active_referrals}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 700, color: p.pending_payout_cents > 0 ? '#4a7c5e' : '#8a94a6' }}>
                          {fmt(p.pending_payout_cents)}
                        </td>
                        <td style={{ padding: '12px 16px' }}><StatusBadge status={p.status} /></td>
                        <td style={{ padding: '12px 16px', fontSize: 12, color: '#8a94a6' }}>
                          {new Date(p.applied_at).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <ChevronDown size={16} color="#8a94a6" style={{ transform: 'rotate(-90deg)' }} />
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
